import { ReactNode, createContext, useContext, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { useWatchSocketConnection } from 'features/hmi/hooks/useWatchSocketConnection';
import { UseWatchSocketReturn, useLiveDataTimeout } from 'greener-ui-library';

export type WatchSocketData = UseWatchSocketReturn & { lastReceivedAt?: Date };

const WatchSocketContext = createContext<WatchSocketData | null>(null);

/** Holds one watch connection for the whole HMI tab set so every tab reads the same stream. */
export const WatchSocketProvider = ({ children }: { children: ReactNode }) => {
  const { unitExternalIdentifier } = useParams();
  const watchSocket = useWatchSocketConnection(unitExternalIdentifier);
  const { lastReceivedAt } = useLiveDataTimeout(watchSocket.messages.timeseries);
  const { isConnected, sendCommand } = watchSocket;
  const hasStateData = watchSocket.messages.state !== undefined;
  const hasRequestedRef = useRef(false);

  useEffect(() => {
    hasRequestedRef.current = false;
  }, [isConnected, unitExternalIdentifier]);

  useEffect(() => {
    if (isConnected && !hasStateData && !hasRequestedRef.current) {
      hasRequestedRef.current = true;
      sendCommand('publish_state_data').catch(() => undefined);
    }
  }, [isConnected, hasStateData, sendCommand, unitExternalIdentifier]);

  return (
    <WatchSocketContext.Provider value={{ ...watchSocket, lastReceivedAt }}>{children}</WatchSocketContext.Provider>
  );
};

export const useWatchSocketData = (): WatchSocketData => {
  const context = useContext(WatchSocketContext);

  if (!context) {
    throw new Error('useWatchSocketData must be used inside a WatchSocketProvider');
  }

  return context;
};
